import React, { useState } from 'react';
import { Autocomplete, IconButton, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../ConfirmPopup/ConfirmPopup';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './PrescriptionPopup.module.css';

const TITLE_MAX = 120;

/**
 * Modelos de receita da clínica dentro do editor: busca pelo nome, "Inserir"
 * coloca o texto do modelo na receita, "Salvar como modelo" guarda o texto
 * atual com um nome e a lixeira apaga o modelo escolhido.
 *
 * `templates`: modelos da clínica; `onTemplatesChange(list)`: lista atualizada
 * depois de criar ou apagar; `onInsert(content)`; `content`: texto atual.
 */
const PrescriptionTemplatePicker = ({ templates, onTemplatesChange, onInsert, content, onError }) => {
  const { SetPrescriptionTemplate, DeletePrescriptionTemplate } = DataProviderInstance();
  const [selected, setSelected] = useState(null);
  const [savingTitle, setSavingTitle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const list = Array.isArray(templates) ? templates : [];
  const hasContent = String(content || '').trim() !== '';

  function handleListResponse(response, fallbackError, after) {
    setBusy(false);
    if (response && response.status === 'SUCCESS' && Array.isArray(response.data)) {
      onTemplatesChange(response.data);
      if (after) after(response.data);
    } else {
      onError(response?.error || fallbackError);
    }
  }

  function saveTemplate() {
    const title = (savingTitle || '').trim();
    if (!title) {
      onError('Dê um nome ao modelo.');
      return;
    }
    setBusy(true);
    SetPrescriptionTemplate({ title, content }, (response) => handleListResponse(response, 'Não foi possível salvar o modelo.', (updated) => {
      setSavingTitle(null);
      setSelected(updated.find((item) => item.title === title) || null);
    }));
  }

  function deleteTemplate() {
    setConfirmDelete(false);
    setBusy(true);
    DeletePrescriptionTemplate(selected.id, (response) => handleListResponse(response, 'Não foi possível apagar o modelo.', () => setSelected(null)));
  }

  if (savingTitle !== null) {
    return (
      <div className={styles.templates}>
        <TextField
          size="small"
          label="Nome do modelo"
          value={savingTitle}
          autoFocus
          onChange={(event) => setSavingTitle(event.target.value.slice(0, TITLE_MAX))}
          onKeyDown={(event) => { if (event.key === 'Enter') saveTemplate(); }}
          className={styles.templateField}
        />
        <CustomButton variant="outlined" style="secondary" label="Cancelar" onClick={() => setSavingTitle(null)} enabled={!busy} />
        <CustomButton variant="contained" style="primary" label="Salvar modelo" onClick={saveTemplate} loading={busy} enabled={!busy} />
      </div>
    );
  }

  return (
    <div className={styles.templates}>
      <Autocomplete
        size="small"
        options={list}
        value={selected}
        onChange={(event, value) => setSelected(value)}
        getOptionLabel={(option) => option.title || ''}
        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
        noOptionsText={list.length === 0 ? 'A clínica ainda não tem modelos' : 'Nenhum modelo com esse nome'}
        className={styles.templateField}
        renderInput={(params) => <TextField {...params} label="Modelo" placeholder="Buscar pelo nome" />}
      />
      <CustomButton
        variant="outlined"
        style="secondary"
        label="Inserir"
        icon={<PlaylistAddOutlinedIcon />}
        enabled={!!selected && !busy}
        onClick={() => onInsert(selected.content)}
      />
      {selected && (
        <Tooltip title="Apagar este modelo">
          <span>
            <IconButton size="small" aria-label="Apagar modelo" onClick={() => setConfirmDelete(true)} disabled={busy}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <CustomButton
        variant="text"
        style="secondary"
        label="Salvar como modelo"
        icon={<BookmarkAddOutlinedIcon />}
        enabled={hasContent && !busy}
        onClick={() => setSavingTitle('')}
      />

      {confirmDelete && (
        <ConfirmPopup
          Visibility={true}
          Title="Apagar modelo"
          Description={`Apagar o modelo "${selected.title}"? As receitas já emitidas com ele não mudam.`}
          ConfirmLabel="Apagar"
          OnConfirm={deleteTemplate}
          OnHide={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
};

export default PrescriptionTemplatePicker;
